var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useState, useMemo, useRef } from "react";
import { Box, Tab, Tabs, useMediaQuery, useTheme, Skeleton, Stack, } from "@mui/material";
import { useParams } from "react-router-dom";
import { FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { PageContainer, PageContent } from "../../../components/wraper";
import PageHeader from "@ui/components/header/PageHeader";
import { useNodeData, useNodeForm } from "../hooks";
import { GeneralSettings, AlarmSettings } from "./components";
// Helper function to safely parse number, returning undefined if NaN or invalid
const safeParseInt = (value) => {
    if (value === undefined || value === null)
        return undefined;
    if (typeof value === "number")
        return value;
    const parsed = parseInt(value, 10);
    return isNaN(parsed) ? undefined : parsed;
};
const safeParseFloat = (value) => {
    if (!value)
        return undefined;
    const parsed = parseFloat(value);
    return isNaN(parsed) ? undefined : parsed;
};
// Map NodesUpdateDto to NodeData for form compatibility
const mapNodeDtoToNodeData = (node) => {
    var _a, _b, _c, _d;
    if (!node)
        return null;
    const mappedData = {
        nodeCategories: (_a = node.nodeCategories) !== null && _a !== void 0 ? _a : [],
        id: node.id,
        nodeId: node.nodeUid,
        nodeName: node.nodeName || "",
        deviceId: node.accessPointUid || "",
        deviceName: "",
        status: node.isActive ? "active" : "inactive",
        isActive: node.isActive,
        nodeDetails: (_b = node.nodeDetails) !== null && _b !== void 0 ? _b : undefined,
        temperature: undefined,
        nodeTypeCode: node.nodeTypeCode,
        lastUpdated: node.lastUpdated,
        // Common configuration fields (now at top level)
        doorAlarmDelay: safeParseInt(node.doorAlaramDelay),
        tempAlarmDelay: safeParseInt(node.tempAlaramDelay),
        installationDate: node.installationDate,
        // Type-specific configuration
        nodeTypeTwoConfig: node.nodeTypeTwoConfig,
        nodeTypeOneConfig: node.nodeTypeOneConfig,
        nodeTypeThreeConfig: node.nodeTypeThreeConfig,
        alarmSettings: node.alaramSettings,
        nodeBehaviour: node.nodeBehaviour,
        freeRoamingNode: (_c = node.freeRoamingNode) !== null && _c !== void 0 ? _c : false,
        isNewHardwareVariant: (_d = node.isNewHardwareVariant) !== null && _d !== void 0 ? _d : false,
    };
    return mappedData;
};
const TabPanel = (_a) => {
    var { children, value, index } = _a, other = __rest(_a, ["children", "value", "index"]);
    return (React.createElement("div", Object.assign({ role: "tabpanel", hidden: value !== index, id: `node-tabpanel-${index}`, "aria-labelledby": `node-tab-${index}` }, other), value === index && React.createElement(Box, { sx: { py: 1 } }, children)));
};
const EditNodePage = () => {
    const { t } = useTranslation(["form", "validation", "common"]);
    const { nodeId } = useParams();
    const [currentTab, setCurrentTab] = useState(0);
    const [alarmRecipientSaveBlocked, setAlarmRecipientSaveBlocked] = useState(false);
    const alarmSettingsRef = useRef(null);
    const { isEdit, node, nodeExists, loading } = useNodeData(nodeId);
    const mappedNode = useMemo(() => mapNodeDtoToNodeData(node), [node]);
    const { methods, control, errors, handleSubmit, validationRules, handleFormSubmit, isSubmitting, } = useNodeForm({ isEdit, node: mappedNode });
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const handleTabChange = (event, newValue) => {
        setCurrentTab(newValue);
    };
    const handleSaveClick = () => {
        var _a;
        if (currentTab === 0) {
            (_a = alarmSettingsRef.current) === null || _a === void 0 ? void 0 : _a.submitForm();
        }
        else if (currentTab === 1) {
            handleSubmit(handleFormSubmit)();
        }
    };
    if (isEdit && loading) {
        return (React.createElement(PageContainer, null,
            React.createElement(Box, { sx: { p: 3 } },
                React.createElement(Stack, { direction: "row", alignItems: "center", spacing: 2, sx: { mb: 3 } },
                    React.createElement(Skeleton, { variant: "circular", width: 40, height: 40 }),
                    React.createElement(Skeleton, { variant: "rectangular", width: 200, height: 32, sx: { borderRadius: 1 } }))),
            React.createElement(PageContent, null,
                React.createElement(Stack, { spacing: 3 },
                    React.createElement(Skeleton, { variant: "rectangular", height: 48, sx: { borderRadius: 1 } }),
                    React.createElement(Box, null,
                        React.createElement(Skeleton, { variant: "text", width: 120, height: 32, sx: { mb: 1.5 } }),
                        React.createElement(Stack, { direction: "row", spacing: 1.5, sx: { mb: 2 } },
                            React.createElement(Skeleton, { variant: "rectangular", height: 56, sx: { flex: 1, borderRadius: 1 } }),
                            React.createElement(Skeleton, { variant: "rectangular", height: 56, sx: { flex: 1, borderRadius: 1 } }),
                            React.createElement(Skeleton, { variant: "rectangular", height: 56, sx: { flex: 1, borderRadius: 1 } }))),
                    React.createElement(Box, null,
                        React.createElement(Skeleton, { variant: "text", width: 100, height: 32, sx: { mb: 1.5 } }),
                        React.createElement(Stack, { spacing: 1.5 },
                            React.createElement(Stack, { direction: "row", spacing: 1.5 },
                                React.createElement(Skeleton, { variant: "rectangular", height: 56, sx: { flex: 1, borderRadius: 1 } }),
                                React.createElement(Skeleton, { variant: "rectangular", height: 56, sx: { flex: 1, borderRadius: 1 } }),
                                React.createElement(Skeleton, { variant: "rectangular", height: 56, sx: { flex: 1, borderRadius: 1 } })),
                            React.createElement(Stack, { direction: "row", spacing: 1.5 },
                                React.createElement(Skeleton, { variant: "rectangular", height: 56, sx: { flex: 1, borderRadius: 1 } }),
                                React.createElement(Skeleton, { variant: "rectangular", height: 56, sx: { flex: 1, borderRadius: 1 } }),
                                React.createElement(Box, { sx: { flex: 1 } })))),
                    React.createElement(Box, null,
                        React.createElement(Skeleton, { variant: "text", width: 220, height: 32, sx: { mb: 1.5 } }),
                        React.createElement(Stack, { direction: "row", spacing: 1.5 },
                            React.createElement(Skeleton, { variant: "rectangular", height: 56, sx: { flex: 1, borderRadius: 1 } }),
                            React.createElement(Skeleton, { variant: "rectangular", height: 56, sx: { flex: 1, borderRadius: 1 } }),
                            React.createElement(Skeleton, { variant: "rectangular", height: 56, sx: { flex: 1, borderRadius: 1 } })))))));
    }
    // Early return for node not found
    if (isEdit && !nodeExists) {
        return (React.createElement(PageContainer, null,
            React.createElement(PageHeader, { title: t("common:pages.nodeNotFound"), showBackButton: true })));
    }
    return (React.createElement(PageContainer, null,
        React.createElement(PageHeader, { title: isEdit ? t("common:pages.editNode") : t("common:pages.addNode"), showBackButton: true, bottomActionButton: {
                label: t("common:actions.saveChanges"),
                onClick: handleSaveClick,
                variant: "contained",
                size: "large",
                disabled: isSubmitting ||
                    (currentTab === 0 && alarmRecipientSaveBlocked),
            } }),
        React.createElement(PageContent, { sx: { pb: 0, pr: 0 } },
            React.createElement(FormProvider, Object.assign({}, methods),
                React.createElement(Box, { sx: { width: "100%" } },
                    React.createElement(Box, null,
                        React.createElement(Tabs, { value: currentTab, onChange: handleTabChange },
                            React.createElement(Tab, { label: t("common:tabs.alarmSettings"), id: "node-tab-0" }),
                            React.createElement(Tab, { label: t("common:tabs.generalSettings"), id: "node-tab-1" }))),
                    React.createElement(Box, { sx: {
                            maxHeight: `calc(100vh - ${isMobile ? 205 : 215}px)`,
                            minHeight: `calc(100vh - ${isMobile ? 205 : 215}px)`,
                            overflowY: "auto",
                            pb: 7,
                            pr: { xs: 1.5, sm: 2, lg: 3 },
                        } },
                        React.createElement(TabPanel, { value: currentTab, index: 0 },
                            React.createElement(AlarmSettings, { ref: alarmSettingsRef, isEdit: isEdit, node: mappedNode, onRecipientSaveBlockedChange: setAlarmRecipientSaveBlocked })),
                        React.createElement(TabPanel, { value: currentTab, index: 1 },
                            React.createElement(GeneralSettings, { control: control, errors: errors, validationRules: validationRules, isEdit: isEdit, node: node, nodeTypeCode: node === null || node === void 0 ? void 0 : node.nodeTypeCode, isNewHardwareVariant: node === null || node === void 0 ? void 0 : node.isNewHardwareVariant }))))))));
};
export default EditNodePage;
